(() => {
  const SELECTOR = '[data-starumi-purchase-modal]';

  const initializeModal = (modal) => {
    if (!modal || modal.dataset.initialized === 'true') return;
    modal.dataset.initialized = 'true';
    if (modal.parentElement !== document.body) document.body.appendChild(modal);

    const form = document.getElementById(modal.dataset.productFormId);
    const opener = form?.querySelector('[data-starumi-product-buy-now]');
    const originalAdd = form?.querySelector('[data-btn-addtocart]');
    const dialog = modal.querySelector('[role="dialog"]');
    const image = modal.querySelector('[data-spm-image]');
    const price = modal.querySelector('[data-spm-price]');
    const quantityInput = modal.querySelector('[data-spm-quantity]');
    const minus = modal.querySelector('[data-spm-quantity-minus]');
    const plus = modal.querySelector('[data-spm-quantity-plus]');
    const add = modal.querySelector('[data-spm-add]');
    const buy = modal.querySelector('[data-spm-buy]');
    const cart = modal.querySelector('[data-spm-cart]');
    const status = modal.querySelector('[data-spm-status]');
    const lowStock = modal.querySelector('[data-spm-low-stock]');
    const product = JSON.parse(modal.querySelector('[data-spm-product-json]')?.textContent || '{}');
    const customProduct = JSON.parse(modal.querySelector('[data-spm-custom-product-json]')?.textContent || '{}');
    const modeButtons = [...modal.querySelectorAll('[data-spm-mode]')];
    const existingSections = [...modal.querySelectorAll('[data-spm-existing-section]')];
    const customSection = modal.querySelector('[data-spm-custom-section]');
    const customAmountInput = modal.querySelector('[data-spm-amount]');
    const customAmountMinus = modal.querySelector('[data-spm-amount-minus]');
    const customAmountPlus = modal.querySelector('[data-spm-amount-plus]');
    const customUnitPrice = modal.querySelector('[data-spm-custom-unit-price]');
    const customTotal = modal.querySelector('[data-spm-custom-total]');
    const customNote = modal.querySelector('[data-spm-custom-note]');

    if (!form || !opener || !originalAdd || !dialog || !product.variants?.length) return;

    let selectedOptions = [...(product.variants.find((variant) => String(variant.id) === String(product.selectedVariantId))?.options || product.variants[0].options)];
    let selectedVariant = null;
    let lastFocused = null;
    let submitting = false;
    let purchaseMode = 'existing';

    const showError = (message) => {
      status.textContent = message || '';
      if (message && window.halo?.showWarning) window.halo.showWarning(message);
    };

    const validateTerms = () => {
      const terms = form.querySelector('.global-checkbox--input');
      if (!terms || terms.checked) return true;
      terms.focus({ preventScroll: false });
      showError(terms.labels?.[0]?.textContent?.trim() || 'Please accept the terms and conditions.');
      return false;
    };

    const validateExistingForm = () => {
      if (!form.checkValidity()) {
        form.reportValidity();
        return false;
      }
      return validateTerms();
    };

    const formatMoney = (cents) => {
      if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(cents, modal.dataset.moneyFormat);
      return (Number(cents || 0) / 100).toFixed(2);
    };

    const getVariant = () => product.variants.find((variant) =>
      variant.options.every((value, index) => value === selectedOptions[index])
    );

    const valueCanResolve = (optionIndex, value) => product.variants.some((variant) =>
      variant.available && variant.options.every((variantValue, index) =>
        index === optionIndex ? variantValue === value : variantValue === selectedOptions[index]
      )
    );

    const quantityLimits = () => {
      const rule = selectedVariant?.quantityRule || {};
      const min = Number(rule.min) || 1;
      const increment = Number(rule.increment) || 1;
      let max = rule.max == null ? Infinity : Number(rule.max);
      if (
        selectedVariant?.inventoryManagement &&
        selectedVariant.inventoryPolicy !== 'continue' &&
        Number.isFinite(Number(selectedVariant.inventoryQuantity))
      ) {
        max = Math.min(max, Math.max(0, Number(selectedVariant.inventoryQuantity)));
      }
      return { min, max, increment };
    };

    const normalizeQuantity = () => {
      const { min, max, increment } = quantityLimits();
      let value = Number.parseInt(quantityInput.value, 10);
      if (!Number.isFinite(value)) value = min;
      value = Math.max(min, value);
      value = min + Math.round((value - min) / increment) * increment;
      if (Number.isFinite(max)) value = Math.min(max, value);
      quantityInput.value = String(value);
      quantityInput.min = String(min);
      quantityInput.step = String(increment);
      if (Number.isFinite(max)) quantityInput.max = String(max);
      else quantityInput.removeAttribute('max');
      minus.disabled = value <= min;
      plus.disabled = Number.isFinite(max) && value >= max;
      return value;
    };

    const normalizeCustomAmount = () => {
      if (!customAmountInput) return 0;
      const minimum = Math.max(1, Number.parseInt(customProduct.minimum, 10) || 1);
      const maximum = Math.max(minimum, Number.parseInt(customProduct.maximum, 10) || 999);
      const raw = String(customAmountInput.value || '').trim();
      const value = Number(raw);
      const valid = /^\d+$/.test(raw) && Number.isInteger(value) && value >= minimum && value <= maximum;
      customAmountInput.min = String(minimum);
      customAmountInput.max = String(maximum);
      customAmountInput.step = '1';
      customAmountMinus.disabled = !valid || value <= minimum;
      customAmountPlus.disabled = !valid || value >= maximum;
      if (customTotal) customTotal.textContent = valid ? formatMoney(customProduct.price * value) : '—';
      return valid ? value : 0;
    };

    const customModeAvailable = () =>
      modal.dataset.customAmountEnabled === 'true' &&
      customProduct.configured === true &&
      customProduct.available === true &&
      Boolean(customProduct.variantId) &&
      normalizeCustomAmount() > 0;

    const syncMainForm = () => {
      if (!selectedVariant || purchaseMode !== 'existing') return;
      const idInput = form.querySelector('input[name="id"]');
      if (idInput && idInput.value !== String(selectedVariant.id)) {
        idInput.value = String(selectedVariant.id);
        idInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      const pageQuantity = form.querySelector('[name="quantity"]');
      if (pageQuantity) {
        pageQuantity.value = quantityInput.value;
        pageQuantity.dispatchEvent(new Event('change', { bubbles: true }));
      }
    };

    const render = () => {
      selectedVariant = getVariant();
      const existingAvailable = Boolean(selectedVariant?.available);
      const variantImage = selectedVariant?.image || product.featuredImage;

      modal.querySelectorAll('[data-spm-option]').forEach((button) => {
        const optionIndex = Number(button.dataset.optionIndex);
        const selected = selectedOptions[optionIndex] === button.dataset.optionValue;
        const available = valueCanResolve(optionIndex, button.dataset.optionValue);
        button.classList.toggle('is-selected', selected);
        button.classList.toggle('is-unavailable', !available);
        button.setAttribute('aria-pressed', String(selected));
        button.disabled = !available && !selected;
      });

      if (purchaseMode === 'custom') {
        if (customProduct.image) image.src = customProduct.image;
        price.textContent = customProduct.priceFormatted || '';
        if (customUnitPrice) customUnitPrice.textContent = customProduct.priceFormatted || '';
      } else if (selectedVariant) {
        price.textContent = selectedVariant.priceFormatted;
        if (variantImage) {
          image.src = variantImage;
          modal.querySelectorAll('[data-spm-spec-image]').forEach((thumb) => { thumb.src = variantImage; });
        }
      }

      const quantity = normalizeQuantity();
      const trackedQuantity = Number(selectedVariant?.inventoryQuantity);
      lowStock.hidden = purchaseMode === 'custom' || !(
        modal.dataset.showLowStock === 'true' &&
        selectedVariant?.inventoryManagement &&
        selectedVariant.inventoryPolicy !== 'continue' &&
        existingAvailable &&
        trackedQuantity > 0 &&
        trackedQuantity <= 5
      );

      const customAvailable = customModeAvailable();
      const actionAvailable = purchaseMode === 'custom' ? customAvailable : existingAvailable;
      const unavailableLabel = purchaseMode === 'custom' ? modal.dataset.customUnavailableMessage : 'Sold out';
      add.disabled = !actionAvailable || submitting;
      buy.disabled = !actionAvailable || submitting;
      add.textContent = purchaseMode === 'custom' ? modal.dataset.addLabel : (actionAvailable ? modal.dataset.addLabel : unavailableLabel);
      buy.textContent = purchaseMode === 'custom' ? modal.dataset.buyLabel : (actionAvailable ? modal.dataset.buyLabel : unavailableLabel);
      if (!actionAvailable) showError(unavailableLabel);
      else if (status.textContent === 'Sold out' || status.textContent === modal.dataset.customUnavailableMessage) showError('');
      syncMainForm();
      return { available: actionAvailable, quantity: purchaseMode === 'custom' ? normalizeCustomAmount() : quantity };
    };

    const setPurchaseMode = (mode) => {
      purchaseMode = mode === 'custom' ? 'custom' : 'existing';
      modeButtons.forEach((button) => {
        const selected = button.dataset.spmMode === purchaseMode;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', String(selected));
      });
      existingSections.forEach((section) => { section.hidden = purchaseMode === 'custom'; });
      if (customSection) customSection.hidden = purchaseMode !== 'custom';
      showError('');
      render();
    };

    const open = () => {
      lastFocused = document.activeElement;
      const formVariantId = form.querySelector('input[name="id"]')?.value;
      const formVariant = product.variants.find((variant) => String(variant.id) === String(formVariantId));
      if (formVariant) selectedOptions = [...formVariant.options];
      const pageQuantity = Number.parseInt(form.querySelector('[name="quantity"]')?.value || '1', 10);
      quantityInput.value = String(Number.isFinite(pageQuantity) ? pageQuantity : 1);
      setPurchaseMode('existing');
      showError('');
      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('starumi-purchase-modal-open');
      window.requestAnimationFrame(() => dialog.focus({ preventScroll: true }));
    };

    const close = () => {
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('starumi-purchase-modal-open');
      if (lastFocused?.focus) lastFocused.focus({ preventScroll: true });
    };

    const checkoutInput = () => {
      if (purchaseMode === 'custom') {
        const note = customNote?.value.trim();
        return {
          lines: [{
            merchandiseId: `gid://shopify/ProductVariant/${customProduct.variantId}`,
            quantity: normalizeCustomAmount(),
            ...(note ? { attributes: [{ key: 'LIVE Item / Order Note', value: note }] } : {})
          }],
          ...(window.Shopify?.country ? { buyerIdentity: { countryCode: window.Shopify.country } } : {})
        };
      }
      const formData = new FormData(form);
      const attributes = new Map();
      for (const [name, value] of formData.entries()) {
        const match = name.match(/^properties\[(.*)\]$/);
        if (!match || value === '') continue;
        if (typeof File !== 'undefined' && value instanceof File) {
          if (value.size > 0) throw new Error('File uploads must be added to the cart before checkout.');
          continue;
        }
        attributes.set(match[1], String(value));
      }
      const sellingPlan = String(formData.get('selling_plan') || '');
      return {
        lines: [{
          merchandiseId: `gid://shopify/ProductVariant/${selectedVariant.id}`,
          quantity: normalizeQuantity(),
          attributes: Array.from(attributes, ([key, value]) => ({ key, value })),
          ...(sellingPlan ? { sellingPlanId: `gid://shopify/SellingPlan/${sellingPlan}` } : {})
        }],
        ...(window.Shopify?.country ? { buyerIdentity: { countryCode: window.Shopify.country } } : {})
      };
    };

    const addCustomAmountToCart = async (quantity) => {
      const properties = {};
      const note = customNote?.value.trim();
      if (note) properties['LIVE Item / Order Note'] = note;
      const response = await fetch(`${window.Shopify?.routes?.root || window.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items: [{ id: customProduct.variantId, quantity, properties }] })
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || result.status) throw new Error(result.description || 'Unable to add this item.');
      if (window.Shopify?.getCart) {
        window.Shopify.getCart((cartState) => {
          document.querySelectorAll('[data-cart-count]').forEach((count) => { count.textContent = cartState.item_count; });
          window.halo?.updateSidebarCart?.(cartState);
          document.body.classList.add('cart-sidebar-show');
        });
      } else {
        document.querySelector('[data-cart-sidebar]')?.click();
      }
    };

    opener.addEventListener('click', open);
    modal.querySelectorAll('[data-spm-close]').forEach((button) => button.addEventListener('click', close));
    modal.querySelectorAll('[data-spm-option]').forEach((button) => button.addEventListener('click', () => {
      selectedOptions[Number(button.dataset.optionIndex)] = button.dataset.optionValue;
      showError('');
      render();
    }));
    modeButtons.forEach((button) => button.addEventListener('click', () => setPurchaseMode(button.dataset.spmMode)));

    minus.addEventListener('click', () => {
      const { increment } = quantityLimits();
      quantityInput.value = String(Number(quantityInput.value) - increment);
      normalizeQuantity();
      syncMainForm();
    });
    plus.addEventListener('click', () => {
      const { increment } = quantityLimits();
      quantityInput.value = String(Number(quantityInput.value) + increment);
      normalizeQuantity();
      syncMainForm();
    });
    quantityInput.addEventListener('change', () => { normalizeQuantity(); syncMainForm(); });

    customAmountMinus?.addEventListener('click', () => {
      const minimum = Math.max(1, Number.parseInt(customProduct.minimum, 10) || 1);
      customAmountInput.value = String(Math.max(minimum, (normalizeCustomAmount() || minimum) - 1));
      render();
    });
    customAmountPlus?.addEventListener('click', () => {
      const maximum = Math.max(1, Number.parseInt(customProduct.maximum, 10) || 999);
      const minimum = Math.max(1, Number.parseInt(customProduct.minimum, 10) || 1);
      customAmountInput.value = String(Math.min(maximum, (normalizeCustomAmount() || minimum) + 1));
      render();
    });
    customAmountInput?.addEventListener('input', render);
    customAmountInput?.addEventListener('change', render);

    add.addEventListener('click', async () => {
      const state = render();
      const valid = purchaseMode === 'custom' ? validateTerms() : validateExistingForm();
      if (!state.available || submitting || !valid) return;
      submitting = true;
      render();
      add.textContent = 'Adding…';
      if (purchaseMode === 'existing') {
        originalAdd.click();
        window.setTimeout(() => {
          submitting = false;
          render();
          close();
        }, 700);
        return;
      }
      try {
        await addCustomAmountToCart(state.quantity);
        submitting = false;
        render();
        close();
      } catch (error) {
        submitting = false;
        showError(error.message || modal.dataset.customUnavailableMessage);
        render();
      }
    });

    buy.addEventListener('click', async () => {
      const state = render();
      const valid = purchaseMode === 'custom' ? validateTerms() : validateExistingForm();
      if (!state.available || submitting || !valid) return;
      submitting = true;
      render();
      buy.textContent = 'Loading…';
      try {
        const query = `mutation StarumiModalBuyNow($input: CartInput!) {
          cartCreate(input: $input) {
            cart { id checkoutUrl totalQuantity }
            userErrors { field message }
          }
        }`;
        const shopDomain = window.Shopify?.shop || window.location.hostname;
        const response = await fetch(`https://${shopDomain}/api/2026-04/graphql.json`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify({ query, variables: { input: checkoutInput() } })
        });
        const result = await response.json().catch(() => ({}));
        const payload = result.data?.cartCreate;
        const error = result.errors?.[0]?.message || payload?.userErrors?.[0]?.message;
        if (!response.ok || error || !payload?.cart?.checkoutUrl) {
          throw new Error(error || 'Unable to create checkout. Please try again.');
        }
        window.location.assign(payload.cart.checkoutUrl);
      } catch (error) {
        submitting = false;
        showError(error.message || 'Unable to create checkout. Please try again.');
        render();
      }
    });

    cart.addEventListener('click', () => {
      close();
      document.querySelector('[data-cart-sidebar]')?.click();
    });

    document.addEventListener('keydown', (event) => {
      if (!modal.classList.contains('is-open')) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = [...modal.querySelectorAll('button:not([disabled]), input:not([disabled]), textarea:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])')]
        .filter((element) => element.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    form.addEventListener('change', () => {
      if (!modal.classList.contains('is-open') || purchaseMode !== 'existing') return;
      const id = form.querySelector('input[name="id"]')?.value;
      const variant = product.variants.find((item) => String(item.id) === String(id));
      if (variant) selectedOptions = [...variant.options];
      render();
    });

    render();
  };

  const initializeAll = () => document.querySelectorAll(SELECTOR).forEach(initializeModal);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initializeAll, { once: true });
  else initializeAll();
  document.addEventListener('shopify:section:load', initializeAll);
})();
